import React, { useEffect, useMemo, useRef, useState } from "react";
import Recurrence from "@/components/Recurrence";
import { Slider } from "@/components/ui/slider";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, TrendingUp } from "lucide-react";

const LEVELS = [
    { key: "apporteur", label: "Apporteur", pct: 0.15 },
    { key: "business_partner", label: "Business Partner", pct: 0.25 },
    { key: "master_partner", label: "Master Partner", pct: 0.3 },
];

const FAQ = [
    {
        q: "Comment sont calculées les commissions ?",
        a: "Ta commission est calculée sur la marge nette générée par le volume réel de tes réseaux. Plus le volume est élevé et plus le mix produit est diversifié, plus tu gagnes.",
    },
    {
        q: "Quand suis-je payé ?",
        a: "Les commissions sont calculées mensuellement et versées le mois suivant, après facturation par ta structure professionnelle. Tout est automatisé via ton espace partenaire.",
    },
    {
        q: "Les revenus sont-ils garantis ?",
        a: "Non. CapDistrib ne garantit pas de revenus fixes. La commission dépend du volume réel généré par tes réseaux et du mix catalogue effectif. C'est une activité commerciale, pas un salaire.",
    },
    {
        q: "Que se passe-t-il si mon réseau arrête ?",
        a: "Tu perçois la commission sur le volume effectivement généré. Pas de pénalité, pas d'engagement de volume minimum côté Apporteur et Business Partner.",
    },
];

function formatEUR(n) {
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(n);
}

// CountUp hook — smoothly animates from previous to current value.
function useCountUp(value, duration = 600) {
    const [display, setDisplay] = useState(value);
    const fromRef = useRef(value);
    const startRef = useRef(null);
    useEffect(() => {
        startRef.current = null;
        const from = fromRef.current;
        const to = value;
        let raf = 0;
        const step = (t) => {
            if (!startRef.current) startRef.current = t;
            const p = Math.min(1, (t - startRef.current) / duration);
            // easeOutQuart
            const eased = 1 - Math.pow(1 - p, 4);
            setDisplay(from + (to - from) * eased);
            if (p < 1) raf = requestAnimationFrame(step);
            else fromRef.current = to;
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value, duration]);
    return display;
}

export default function Simulator({ onJoin }) {
    const [networks, setNetworks] = useState(3);
    const [volume, setVolume] = useState(40000);
    const [levelKey, setLevelKey] = useState("business_partner");

    const level = LEVELS.find((l) => l.key === levelKey) || LEVELS[1];
    const MARGE_RATE = 0.02;

    const totals = useMemo(() => {
        const totalVolume = networks * volume;
        const marge = totalVolume * MARGE_RATE;
        const commission = marge * level.pct;
        return {
            totalVolume,
            marge,
            commission,
            yearly: commission * 12,
        };
    }, [networks, volume, level]);

    const animCommission = useCountUp(totals.commission);
    const animYearly = useCountUp(totals.yearly);
    const animVolume = useCountUp(totals.totalVolume);
    const animMarge = useCountUp(totals.marge);

    const highCommission = totals.commission >= 2500;

    return (
        <section
            id="revenus"
            className="relative py-24 sm:py-32 bg-cap-ink text-white overflow-hidden"
            data-testid="simulator-section"
        >
            <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
            <div className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-cap-violet/30 blur-[140px]" />
            <div className="absolute -bottom-32 left-0 w-[420px] h-[420px] rounded-full bg-cap-gold/15 blur-[140px]" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <p className="fade-up eyebrow">Vos revenus</p>
                    <h2
                        className="fade-up h-display text-3xl sm:text-4xl lg:text-[56px] font-bold mt-4"
                        style={{ animationDelay: "0.05s" }}
                        data-testid="simulator-title"
                    >
                        Estime tes revenus
                        <br className="hidden sm:block" />{" "}
                        <span className="text-violet-glow">potentiels</span>.
                    </h2>
                    <p
                        className="fade-up mt-6 text-lg text-white/65 leading-relaxed"
                        style={{ animationDelay: "0.1s" }}
                    >
                        Basé sur le volume réel de tes réseaux partenaires. Une
                        estimation indicative, pas une promesse de gain.
                    </p>
                </div>

                <div
                    className="fade-up mt-14 grid lg:grid-cols-[1.1fr_1fr] gap-6 lg:gap-8"
                    data-testid="simulator-card"
                >
                    {/* Controls */}
                    <div className="glass p-7 sm:p-9">
                        <p className="eyebrow">Paramètres</p>
                        <h3 className="h-display text-xl font-semibold text-white mt-1">
                            Construis ton scénario
                        </h3>

                        <div className="mt-8" data-testid="slider-networks">
                            <div className="flex items-baseline justify-between mb-3">
                                <label className="text-[14px] font-medium text-white/85">
                                    Nombre de réseaux dans ton portefeuille
                                </label>
                                <span className="dashboard-num text-cap-violetSoft font-bold text-lg">
                                    {networks}
                                </span>
                            </div>
                            <Slider
                                value={[networks]}
                                min={1}
                                max={15}
                                step={1}
                                onValueChange={(v) => setNetworks(v[0])}
                                data-testid="slider-networks-input"
                            />
                            <div className="flex justify-between text-[11px] text-white/40 mt-2">
                                <span>1</span>
                                <span>15</span>
                            </div>
                        </div>

                        <div className="mt-8" data-testid="slider-volume">
                            <div className="flex items-baseline justify-between mb-3">
                                <label className="text-[14px] font-medium text-white/85">
                                    Volume mensuel moyen par réseau
                                </label>
                                <span className="dashboard-num text-cap-violetSoft font-bold text-lg">
                                    {formatEUR(volume)}
                                </span>
                            </div>
                            <Slider
                                value={[volume]}
                                min={15000}
                                max={300000}
                                step={5000}
                                onValueChange={(v) => setVolume(v[0])}
                                data-testid="slider-volume-input"
                            />
                            <div className="flex justify-between text-[11px] text-white/40 mt-2">
                                <span>15 000 €</span>
                                <span>300 000 €</span>
                            </div>
                        </div>

                        <div className="mt-9">
                            <label className="text-[14px] font-medium text-white/85">
                                Ton niveau
                            </label>
                            <div
                                className="mt-3 grid grid-cols-3 gap-2"
                                data-testid="level-selector"
                            >
                                {LEVELS.map((l) => (
                                    <button
                                        key={l.key}
                                        onClick={() => setLevelKey(l.key)}
                                        className={`relative px-3 py-3 rounded-xl text-[13px] font-semibold transition border ${
                                            levelKey === l.key
                                                ? "bg-cap-violet text-white border-cap-violet shadow-[0_0_24px_-6px_rgba(108,71,255,0.7)]"
                                                : "bg-white/[0.04] text-white/80 border-white/10 hover:border-white/30"
                                        }`}
                                        data-testid={`level-btn-${l.key}`}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Output — featured card with violet→gold gradient bg */}
                    <div
                        className="relative rounded-[20px] overflow-hidden p-7 sm:p-9 border border-white/10"
                        style={{
                            background:
                                "linear-gradient(155deg, rgba(108,71,255,0.20) 0%, rgba(15,15,26,0.6) 55%, rgba(245,158,11,0.18) 100%)",
                            backdropFilter: "blur(14px)",
                        }}
                    >
                        <div
                            className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[100px]"
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(245,158,11,0.5) 0%, transparent 70%)",
                            }}
                        />
                        <div className="relative">
                            <p className="eyebrow">Résultat estimé</p>
                            <h3 className="h-display text-xl font-semibold text-white mt-1">
                                Ton scénario en chiffres
                            </h3>

                            <div className="mt-7 space-y-4">
                                <MetricRow
                                    label="Volume total géré"
                                    value={formatEUR(animVolume)}
                                    sub="par mois"
                                    testid="metric-volume"
                                />
                                <MetricRow
                                    label="Marge nette estimée"
                                    value={formatEUR(animMarge)}
                                    sub="marge nette (≈ 2%)"
                                    testid="metric-marge"
                                />
                            </div>

                            {/* HERO commission — large dashboard number */}
                            <div
                                className="mt-6 p-6 rounded-2xl border border-white/10 bg-white/[0.03] relative overflow-hidden"
                                data-testid="metric-commission"
                            >
                                <p className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-semibold">
                                    Ta commission mensuelle —{" "}
                                    <span className="text-cap-violetSoft">
                                        {level.label}
                                    </span>
                                </p>
                                <p
                                    className={`dashboard-num mt-2 text-5xl sm:text-[56px] font-bold leading-none tracking-tight transition-all duration-300 ${
                                        highCommission
                                            ? "text-cap-gold [text-shadow:0_0_30px_rgba(245,158,11,0.45)]"
                                            : "text-white"
                                    }`}
                                >
                                    {formatEUR(animCommission)}
                                </p>
                                <p className="mt-3 text-[13px] text-white/55">
                                    soit{" "}
                                    <span
                                        className="dashboard-num text-white font-semibold"
                                        data-testid="metric-yearly"
                                    >
                                        {formatEUR(animYearly)}
                                    </span>{" "}
                                    sur 12 mois
                                </p>
                                <p
                                    className="mt-1 text-[13px] text-white/55"
                                    data-testid="metric-3y-wrapper"
                                >
                                    soit{" "}
                                    <span
                                        className="dashboard-num text-cap-gold font-semibold"
                                        data-testid="metric-36months"
                                    >
                                        {formatEUR(animYearly * 3)}
                                    </span>{" "}
                                    sur 36 mois
                                </p>
                            </div>

                            <button
                                onClick={onJoin}
                                className="mt-6 btn-gold w-full py-3.5 rounded-full text-sm inline-flex items-center justify-center gap-2"
                                data-testid="simulator-cta"
                            >
                                Rejoindre le programme
                                <ArrowRight size={16} />
                            </button>

                            <p className="mt-4 text-[12px] text-white/55 italic leading-relaxed">
                                Ces revenus sont récurrents tant que tes réseaux
                                restent actifs.
                            </p>
                            <p className="mt-2 text-[12px] text-white/40 italic leading-relaxed">
                                Estimation indicative basée sur un mix produit
                                standard. Les commissions réelles dépendent du
                                volume et du mix catalogue effectifs.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Recurrence — 3-year accumulation chart */}
                <Recurrence />

                {/* Scenarios — light cards on dark bg */}
                <div className="mt-20">
                    <p className="fade-up eyebrow">Scénarios concrets</p>
                    <h3
                        className="fade-up h-display text-2xl sm:text-3xl font-bold text-white mt-3"
                        style={{ animationDelay: "0.05s" }}
                    >
                        Voici à quoi ressemble ton revenu, vraiment.
                    </h3>

                    <div className="mt-10 grid md:grid-cols-3 gap-6">
                        <ScenarioCard
                            tone="violet"
                            label="Scénario 1"
                            title="Tu apportes 1 réseau B2B"
                            volume="15 000 € / mois"
                            level="Business Partner"
                            marge={300}
                            commission={75}
                            insight="Un premier réseau, une première récurrence."
                        />
                        <ScenarioCard
                            tone="gold"
                            label="Scénario 2"
                            title="Tu gères 3 réseaux actifs"
                            volume="120 000 € / mois"
                            level="Business Partner"
                            marge={2400}
                            commission={600}
                            insight="Un portefeuille qui tourne, un revenu qui s'installe."
                            featured
                        />
                        <ScenarioCard
                            tone="white"
                            label="Scénario 3"
                            title="Tu développes une zone"
                            volume="300 000 € / mois"
                            level="Master Partner"
                            marge={6000}
                            commission={1800}
                            insight="Une zone bien travaillée, un revenu significatif."
                        />
                    </div>

                    <p className="mt-8 text-[13px] text-white/55 italic max-w-3xl leading-relaxed">
                        Estimation basée sur un mix produit standard et une
                        marge nette indicative de 2%. Les revenus réels
                        dépendent du volume et du catalogue effectivement
                        activé sur chaque réseau.
                    </p>
                </div>

                <div className="mt-24 grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-14">
                    <div className="fade-up">
                        <p className="eyebrow">FAQ Revenus</p>
                        <h3 className="h-display mt-3 text-2xl sm:text-3xl font-bold text-white">
                            Tes questions sur les revenus.
                        </h3>
                        <p className="mt-3 text-white/55">
                            Transparent et honnête : ce n'est pas un salaire,
                            c'est une activité commerciale.
                        </p>
                    </div>
                    <Accordion
                        type="single"
                        collapsible
                        className="fade-up"
                        data-testid="revenus-faq"
                    >
                        {FAQ.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`r-${i}`}
                                className="border-b border-white/10"
                            >
                                <AccordionTrigger
                                    className="text-left text-[15.5px] font-semibold text-white hover:text-cap-violetSoft py-5"
                                    data-testid={`revenus-faq-trigger-${i}`}
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent
                                    className="text-white/65 leading-relaxed text-[15px] pb-5"
                                    data-testid={`revenus-faq-content-${i}`}
                                >
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}

function MetricRow({ label, value, sub, testid }) {
    return (
        <div
            className="flex items-end justify-between gap-4 pb-4 border-b border-white/10"
            data-testid={testid}
        >
            <div>
                <p className="text-[13px] text-white/70">{label}</p>
                <p className="text-[11px] text-white/40">{sub}</p>
            </div>
            <p className="dashboard-num text-2xl font-semibold text-white">
                {value}
            </p>
        </div>
    );
}

function ScenarioCard({
    tone = "violet",
    label,
    title,
    volume,
    level,
    marge,
    commission,
    insight,
    featured,
}) {
    const tones = {
        violet:
            "ring-1 ring-cap-violet/30 bg-cap-violet/10 text-cap-violetSoft",
        gold: "ring-1 ring-cap-gold/40 bg-cap-gold/15 text-cap-gold",
        white: "ring-1 ring-white/20 bg-white/[0.06] text-white/85",
    }[tone];
    const fmt = (n) =>
        new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
        }).format(n);

    return (
        <div
            className={`fade-up glass p-7 flex flex-col relative overflow-hidden ${
                featured
                    ? "border-cap-gold/50 shadow-[0_24px_60px_-20px_rgba(245,158,11,0.4)]"
                    : ""
            }`}
            data-testid={`scenario-${tone}`}
        >
            <span
                className={`inline-flex self-start items-center text-[10.5px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full ${tones}`}
            >
                {label}
            </span>
            <h4 className="h-display mt-5 text-lg font-semibold text-white">
                {title}
            </h4>
            <p className="mt-3 text-[14px] text-white/70">
                Volume :{" "}
                <span className="font-semibold text-white">{volume}</span>
            </p>
            <p className="text-[14px] text-white/70">
                Marge nette :{" "}
                <span className="font-semibold text-white">{fmt(marge)}</span>
            </p>
            <p className="text-[14px] text-white/70">
                Niveau :{" "}
                <span className="font-semibold text-white">{level}</span>
            </p>

            <div className="my-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <TrendingUp size={18} className="text-cap-gold" />
                <div className="flex-1 h-px bg-white/10" />
            </div>

            <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-semibold">
                Commission estimée
            </p>
            <p className="dashboard-num mt-1 text-3xl font-bold text-cap-gold">
                {fmt(commission)}{" "}
                <span className="text-sm font-medium text-white/60">
                    / mois
                </span>
            </p>
            <p className="mt-3 text-[13.5px] text-white/55 italic">
                {insight}
            </p>
        </div>
    );
}
